// Dependencies
import React from 'react';
import { useTranslation } from 'next-i18next';

// Material UI Components
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';

// Internal Components
import NavListItem from './NavListItem';
import { useSelector } from 'src/store/store';
import Label from 'src/components/Label';

// Types
type Props = {
  onClick?: () => void;
};

// ----------------------------------------------------------------------
export default function NavigationList({ onClick }: Props) {
  // ====================================
  // Redux State
  const { activeTradingSessionsCount, tariffPromoUsed } = useSelector((state) => state.auth);

  // ====================================
  // Hooks
  const { t } = useTranslation('common');

  return (
    <Box width="100%" flexGrow={1} sx={{ overflowY: 'auto' }}>
      <List disablePadding sx={{ px: 2 }}>
        {process.env.NEXT_PUBLIC_SIMULATOR_ON === 'true' && (
          <NavListItem
            path="/dashboard/simulator"
            title={t('nav.simulator')}
            icon={'fluent:receipt-cube-24-regular'}
            onClick={onClick}
          />
        )}
        <NavListItem
          path="/dashboard/sessions"
          title={t('nav.sessions')}
          icon={'material-symbols-light:timeline'}
          onClick={onClick}
          additional={
            <Label
              color={activeTradingSessionsCount ? 'success' : undefined}
              sx={{
                cursor: 'pointer',
              }}
            >
              {activeTradingSessionsCount || 0}
            </Label>
          }
        />
        <NavListItem
          path="/dashboard/tariffs"
          title={t('nav.tariffs')}
          icon={'ph:seal-percent'}
          onClick={onClick}
          additional={
            tariffPromoUsed ? (
              <Label
                color={'primary'}
                sx={{
                  cursor: 'pointer',
                }}
              >
                %
              </Label>
            ) : undefined
          }
        />
        <Divider sx={{ my: 1 }} />
        <NavListItem
          path="/dashboard/affiliates"
          alternativePaths={[
            '/dashboard/affiliates/summary',
            '/dashboard/affiliates/referrals',
            '/dashboard/affiliates/leaderboard',
            '/dashboard/affiliates/sessions',
            '/dashboard/affiliates/earnings',
            '/dashboard/affiliates/campaigns',
            '/dashboard/affiliates/history',
          ]}
          title={t('nav.affiliates')}
          icon={'tabler:affiliate'}
          onClick={onClick}
        />
        <Divider sx={{ my: 1 }} />
        <NavListItem
          path="/dashboard/wallet"
          title={t('nav.wallet')}
          icon={'solar:wallet-bold'}
          onClick={onClick}
        />
        <NavListItem
          path="/dashboard/wallet/transactions"
          title={t('nav.transactions')}
          icon={'icon-park-outline:funds'}
          onClick={onClick}
        />
        <NavListItem
          path="/dashboard/wallet/orders"
          title={t('nav.orders')}
          icon={'lets-icons:order'}
          onClick={onClick}
        />
      </List>
    </Box>
  );
}
